import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

import { Button } from '../../../components/core';
import { DialogCloseButton } from '../../../components/core/DialogCloseButton';
import { useMerchant } from '../../../contexts/MerchantContext';
import { useCatalogSyncMutation } from '../../../store/modules/ellaDataLayer/api';
import { usePatchStoreReadinessMutation } from '../../../store/modules/merchant/api';
import { toast } from '../../../util/toast';

const getSyncErrorMessage = (err: unknown): string => {
  const error = (err as { data?: { error?: string } })?.data?.error;
  return error || 'Failed to sync catalog, please try again';
};

const formatSyncResult = (productsUpserted: number, skusUpserted: number): string =>
  `Catalog synced: ${productsUpserted.toLocaleString()} products, ${skusUpserted.toLocaleString()} SKUs.`;

/**
 * Triggers a full catalog ingestion for the current merchant.
 * Visible to anyone who can open Agent Hub; disabled without update:agent-hub.
 */
export const CatalogSyncButton = (): JSX.Element => {
  const { merchantId, merchantName, canUpdateAgentHub } = useMerchant();
  const canSync = canUpdateAgentHub();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [catalogSync, { isLoading }] = useCatalogSyncMutation();
  const [patchStoreReadiness] = usePatchStoreReadinessMutation();

  const runSync = async () => {
    if (!merchantId || !canSync) {
      return;
    }

    setConfirmOpen(false);
    const toastId = toast.loading('Syncing catalog. This can take several minutes...');
    try {
      const result = await catalogSync({ merchantId }).unwrap();
      const lastCatalogSyncAt = new Date().toISOString();
      const persistLastCatalogSyncAt = async () => {
        try {
          await patchStoreReadiness({
            merchantId,
            body: { storeReadiness: { lastCatalogSyncAt } },
          }).unwrap();
        } catch {
          await patchStoreReadiness({
            merchantId,
            body: { storeReadiness: { lastCatalogSyncAt } },
          }).unwrap();
        }
      };

      try {
        await persistLastCatalogSyncAt();
      } catch {
        toast.success(formatSyncResult(result.productsUpserted, result.skusUpserted));
        toast.error('Catalog synced, but store readiness could not be updated. Please try again.');
        return;
      }

      toast.success(formatSyncResult(result.productsUpserted, result.skusUpserted));
    } catch (err) {
      toast.error(getSyncErrorMessage(err));
    } finally {
      toast.dismiss(toastId);
    }
  };

  return (
    <>
      {/* Label stays fixed so the button keeps its width while syncing; progress is the spinner
          plus the persistent loading toast. */}
      <Button
        text="Sync catalog"
        theme="secondary"
        disabled={!canSync || isLoading || !merchantId}
        loading={isLoading}
        onClick={() => {
          if (!canSync) {
            return;
          }
          setConfirmOpen(true);
        }}
        dataTestId="ella-catalog-sync-button"
        sx={{
          height: 48,
          minWidth: 125,
          px: 2,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      />

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            minWidth: { xs: '90%', md: '520px' },
            p: 3,
            position: 'relative',
          },
        }}
        data-testid="ella-catalog-sync-confirm"
      >
        <DialogCloseButton onClose={() => setConfirmOpen(false)} />
        <DialogTitle sx={{ fontWeight: 600, p: 0, mb: 1, pr: 5 }}>Sync catalog</DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Typography sx={{ mb: 3, color: 'text.secondary', fontSize: 14 }}>
            This re-ingests the full product catalog for{' '}
            <Typography component="span" fontWeight="bold" sx={{ fontSize: 14 }}>
              {merchantName || 'this merchant'}
            </Typography>
            . Only one sync should run at a time - wait for this one to finish before starting
            another.
          </Typography>

          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={1}
            sx={{ '& button': { height: 48 } }}
          >
            <Button
              text="Cancel"
              theme="secondary"
              onClick={() => setConfirmOpen(false)}
              dataTestId="ella-catalog-sync-cancel-button"
              sx={{ minWidth: 120 }}
            />
            <Button
              text="Start sync"
              theme="primary"
              onClick={runSync}
              dataTestId="ella-catalog-sync-confirm-button"
              sx={{ minWidth: 120 }}
            />
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};
